// React
import React from 'react'

// Next
import Head from 'next/head'
import { GetStaticProps } from 'next'

// Components
import FAQ from '@/components/website/FAQ'
import TextSection from '@/components/website/core/TextSection'
import ApiDocs from '@/components/blocks/api-docs'
import Layout from '@/components/website/core/Layout'
import HeroComp2 from '@/components/website/core/HeroComp_2'
import WhySupermeme from '@/components/website/WhySupermeme'
import MemeAPIUseCases from '@/components/website/MemeAPIUseCases'
import MemeAPICTA from '@/components/website/MemeAPICTA'

// Utils
import { getLandingStaticProps } from '@/utils/ui/landing/get-static-props'

// External
import { useTranslation } from 'next-i18next'

const MemeApi: React.FC = () => {
  const { t } = useTranslation('routes/meme-api')

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'APIReference',
    name: t('meta.title'),
    description: t('meta.description'),
    publisher: {
      '@type': 'Organization',
      name: 'Supermeme.ai',
      logo: {
        '@type': 'ImageObject',
        url: 'https://supermeme.ai/static/logo-big.png',
      },
    },
    offers: {
      '@type': 'Offer',
      category: 'API Services',
    },
    applicationCategory: 'DeveloperApplication',
    programmingLanguage: 'REST API',
    additionalType: 'WebAPI',
  }

  return (
    <Layout>
      <Head>
        <title>{t('meta.title')}</title>
        <meta name='description' content={t('meta.description')} />
        <meta property='og:url' content='https://supermeme.ai/meme-api' />
        <link rel='canonical' href='https://supermeme.ai/meme-api' />
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>
      <HeroComp2
        mainHeading={t('hero.mainHeading')}
        subHeading={t('hero.subHeading')}
        image_src='/static/landing-pages/meme-api/hero.png'
        benefits={
          t('hero.benefits', { returnObjects: true }) as {
            icon: string
            title: string
            description: string
          }[]
        }
      />
      <WhySupermeme
        title={t('whySupermeme.title')}
        subtitle={t('whySupermeme.subtitle')}
        additionalText={t('whySupermeme.additionalText')}
        features={
          t('whySupermeme.features', { returnObjects: true }) as {
            title: string
            description: string
            icon: any
          }[]
        }
      />
      <MemeAPIUseCases />
      <ApiDocs />

      <div className='mx-auto max-w-3xl py-6 sm:py-8'>
        <TextSection
          h2={t('useCases.title')}
          li={(
            t('useCases.list', { returnObjects: true }) as {
              title: string
              description: string
            }[]
          ).map((useCase) => (
            <>
              <strong>{useCase.title}:</strong> {useCase.description}
            </>
          ))}
        />
      </div>

      <div className='mx-auto max-w-3xl py-6 sm:py-8'>
        <TextSection
          h2={t('integration.title')}
          p={t('integration.intro')}
          li={(
            t('integration.list', { returnObjects: true }) as {
              title: string
              description: string
            }[]
          ).map((item) => (
            <>
              <strong>{item.title}:</strong> {item.description}
            </>
          ))}
        />
      </div>

      <MemeAPICTA />

      <FAQ
        faqs={
          t('faqs', { returnObjects: true }) as {
            question: string
            answer: string
          }[]
        }
      />
    </Layout>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return getLandingStaticProps({
    locale,
    namespaces: [
      'common',
      'routes/meme-api',
      'components/meme-api-cta',
      'components/why-supermeme',
      'components/login-form',
    ],
  })
}

export default MemeApi
